// Copyright (C) 2026 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import Popover from 'antd/lib/popover';
import Button from 'antd/lib/button';
import Icon from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';

import { Canvas } from 'cvat-canvas-wrapper';
import {
    DeletePolygonMode,
    enableDeletePolygonDrawingAsync,
} from 'actions/annotation-actions';
import { CombinedState } from 'reducers';

import withVisibilityHandling from './handle-popover-visibility';

export interface Props {
    canvasInstance: Canvas;
    isDrawing: boolean;
    disabled?: boolean;
}

// Simple inline SVG: polygon outline with a slash, signalling destructive action.
const DeletePolygonSVG = (): JSX.Element => (
    <svg width='1em' height='1em' viewBox='0 0 24 24' fill='currentColor'>
        <path
            d='M12 2 L22 9 L18 21 L6 21 L2 9 Z'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinejoin='round'
        />
        <line x1='4' y1='4' x2='20' y2='20' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' />
    </svg>
);

const DeletePolygonIcon = (): JSX.Element => <Icon component={DeletePolygonSVG} />;

const CustomPopover = withVisibilityHandling(Popover, 'delete-polygon');

function DeletePolygonContent(props: {
    onPick: (mode: DeletePolygonMode) => void;
    deleteShortcut?: string;
    cutShortcut?: string;
}): JSX.Element {
    const { onPick, deleteShortcut, cutShortcut } = props;
    return (
        <div
            className='cvat-delete-polygon-popover-content'
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                minWidth: 200,
            }}
        >
            <Button type='primary' danger onClick={() => onPick('delete')}>
                {`Delete shapes inside${deleteShortcut ? ` (${deleteShortcut})` : ''}`}
            </Button>
            <Button onClick={() => onPick('cut')}>
                {`Cut shapes by polygon${cutShortcut ? ` (${cutShortcut})` : ''}`}
            </Button>
            <small style={{ color: '#888' }}>
                Draw the polygon (Shift = equidistant points). Right-click undoes a point. N or Enter finishes.
            </small>
        </div>
    );
}

function DrawDeletePolygonControl(props: Props): JSX.Element {
    const { canvasInstance, isDrawing, disabled } = props;
    const dispatch = useDispatch();
    const normalizedKeyMap = useSelector((state: CombinedState) => state.shortcuts.normalizedKeyMap);
    const deleteShortcut = normalizedKeyMap?.DELETE_POLYGON_FULL_MODE;
    const cutShortcut = normalizedKeyMap?.DELETE_POLYGON_CUT_MODE;

    const onPick = (mode: DeletePolygonMode): void => {
        dispatch(enableDeletePolygonDrawingAsync(mode));
    };

    if (disabled) {
        return (
            <Icon className='cvat-delete-polygon-control cvat-disabled-canvas-control' component={DeletePolygonSVG} />
        );
    }

    if (isDrawing) {
        return (
            <Icon
                className='cvat-delete-polygon-control cvat-active-canvas-control'
                component={DeletePolygonSVG}
                onClick={(): void => {
                    canvasInstance.draw({ enabled: false });
                }}
            />
        );
    }

    return (
        <CustomPopover
            overlayClassName='cvat-delete-polygon-popover'
            placement='right'
            content={(
                <DeletePolygonContent
                    onPick={onPick}
                    deleteShortcut={deleteShortcut}
                    cutShortcut={cutShortcut}
                />
            )}
        >
            <Icon className='cvat-delete-polygon-control' component={DeletePolygonSVG} />
        </CustomPopover>
    );
}

export { DeletePolygonIcon };
export default React.memo(DrawDeletePolygonControl);
